import { ChatViewer } from '@core/chat';
import { PresentationSlides } from '@core/presentation-panel';
import { ClientType, DisconnectReason, LiveStatus, VideoLayout, WebinarStatus } from '@core/types';
import { ErrorMessagePage, Loader, MaintenanceNotice, MobileMenuNavigator, useLayout } from '@core/ui';
import { Chapters } from '@online-webinar/chapters';
import { joinWebinar, leaveWebinar, useAppDispatch, useAppSelector } from '@online-webinar/store';
import { useEffect, useState } from 'react';
import { onExamClose, QuestionnaireOverlay, showSurveyOrExam } from '../../..';
import { ChapterContent } from '../../molecules/ChapterContent';
import { ChatContent } from '../../molecules/ChatContent';
import { PresentationContent } from '../../molecules/PresentationContent';
import { TestWebinarOverlay } from '../../molecules/TestWebinarOverlay';
import { DuplicateSession } from '../DuplicateSession';
import { PollContainer } from '../PollContainer';
import { TestWebinarFull } from '../TestWebinarFull';
import { ViewerLiveWebinarLayout } from '../ViewerLiveWebinarLayout';
import { ViewEventOverlay } from '../ViewEventOverlay';

export const SlideViewerOrganism = ({
    webinarId,
    linkCode,
    anonymousId,
    anonymousAuthorizationCode
}: {
    webinarId: string;
    linkCode?: string;
    anonymousId?: string;
    anonymousAuthorizationCode?: string;
}) => {
    const {
        mainContainerRef,
        chapterActivated,
        chatActivated,
        slidesActivated,
        videoActivated,
        pollsActive,
        onMobileMenuItemClicked,
        gridContentStyles,
        isLandscape,
        mobileMenuEnabled
    } = useLayout();

    const dispatch = useAppDispatch();

    const [previousWebinarStatus, setPreviousWebinarStatus] = useState<WebinarStatus>(WebinarStatus.Planned);

    // Send Join Webinar
    useEffect(() => {
        dispatch(
            joinWebinar({
                webinarId: webinarId,
                linkCode: linkCode,
                anonymousId: anonymousId,
                anonymousAuthorizationCode: anonymousAuthorizationCode,
                clientType: ClientType.slide_viewer
            })
        );
        return () => {
            dispatch(leaveWebinar());
        };
    }, []);

    const isInitialized = useAppSelector((state) => state.liveWebinar.isInitialized);
    const webinarStatus = useAppSelector((state) => state.liveWebinar.webinarData.status);
    const isTest = useAppSelector((state) => state.liveWebinar.webinarData.isTest);
    const dataConnection = useAppSelector((state) => state.liveWebinar.dataConnection);
    const slideDeck = useAppSelector((state) => state.liveWebinar.slideDeck);
    const activeSlideNumber = useAppSelector((state) => state.liveWebinar.activeSlideNumber);
    const chapters = useAppSelector((state) => state.liveWebinar.chapters);
    const activeChapter = useAppSelector((state) => state.liveWebinar.activeChapter);
    const survey = useAppSelector((state) => state.liveWebinar.survey);
    const surveyAnswers = useAppSelector((state) => state.liveWebinar.surveyAnswers);
    const exam = useAppSelector((state) => state.liveWebinar.exam);
    const examAnswers = useAppSelector((state) => state.liveWebinar.examAnswers);
    const unreadChatMessages = useAppSelector((state) => state.liveWebinar.unreadChatMessages);

    const [hasChapters, setHasChapters] = useState(false);
    const [hasSlides, setHasSlides] = useState(false);

    useEffect(() => {
        setHasChapters(chapters?.length !== 0);
    }, [chapters]);

    useEffect(() => {
        setHasSlides(slideDeck?.slides.length !== 0);
    }, [slideDeck]);

    useEffect(() => {
        document.body.classList.add('no-menu');
        return () => {
            document.body.classList.remove('no-menu');
        };
    }, []);

    // Show survey when switching from live to ended
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        setPreviousWebinarStatus(webinarStatus);

        if (previousWebinarStatus === WebinarStatus.Started && webinarStatus === WebinarStatus.Ended) {
            showSurveyOrExam(dispatch, survey, surveyAnswers, exam, examAnswers);
        }

        if (previousWebinarStatus === WebinarStatus.Planned && webinarStatus === WebinarStatus.Ended) {
            showSurveyOrExam(dispatch, survey, surveyAnswers, exam, examAnswers);
        }
    }, [isInitialized, webinarStatus, previousWebinarStatus, survey, surveyAnswers, exam, examAnswers, dispatch]);

    if (dataConnection.status === LiveStatus.Error) {
        const error = dataConnection.error ?? { statusCode: 500, success: false };
        return <ErrorMessagePage apiError={error} />;
    }

    // The hub refuses a full test webinar before it ever sends LiveState, so isInitialized never
    // becomes true for this viewer — this gate must precede the !isInitialized check below.
    if (dataConnection.status === LiveStatus.Offline && dataConnection.disconnectReason === DisconnectReason.TestWebinarFull) {
        return <TestWebinarFull />;
    }

    if (!isInitialized) {
        return <Loader classNames="m-auto" />;
    }

    if (dataConnection.status === LiveStatus.Offline && dataConnection.disconnectReason === DisconnectReason.DuplicateSession) {
        return <DuplicateSession />;
    }

    return (
        <ViewerLiveWebinarLayout
            ref={mainContainerRef}
            isLandscape={isLandscape}
            isMobileEnabled={mobileMenuEnabled}
            banner={<MaintenanceNotice area="live" />}
        >
            <QuestionnaireOverlay onClose={(type) => onExamClose(dispatch, type, survey, surveyAnswers)} />
            {webinarStatus === WebinarStatus.Planned && <ViewEventOverlay />}
            <div className={`col-start-1 row-start-1 ${mobileMenuEnabled ? (isLandscape ? '' : 'max-h-96') : 'max-h-[calc(100dvh-4rem)] bg-black'} min-h-0`}>
                <svg
                    width="1920"
                    height="1080"
                    viewBox="0 0 1920 1080"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-auto max-w-full max-h-full"
                    fill="transparent"
                >
                    <rect width="1920" height="1080" />
                </svg>
            </div>
            <TestWebinarOverlay isTest={isTest} />
            {slidesActivated || !mobileMenuEnabled ? (
                <PresentationContent isLandscape={isLandscape} isMobile={mobileMenuEnabled} videoLayout={VideoLayout.PresentationWithVideoSmall}>
                    <PresentationSlides
                        slides={slideDeck?.slides ?? []}
                        currentSlideNumber={activeSlideNumber ?? 0}
                        classNames={!isLandscape ? 'object-top' : ''}
                        showFullScreen="true"
                        videoLayout={VideoLayout.PresentationWithVideoSmall}
                    />
                </PresentationContent>
            ) : null}
            <div
                className={`${mobileMenuEnabled ? (isLandscape ? 'row-start-1 col-start-1' : 'row-start-2 col-start-1') : 'row-span-full col-start-2'} grid grid-rows-[auto_1fr]`}
            >
                {chapterActivated || !mobileMenuEnabled ? (
                    <ChapterContent isLandscape={isLandscape} isMobile={mobileMenuEnabled}>
                        {hasChapters && <Chapters chapters={chapters} activeChapter={activeChapter} showHeader={mobileMenuEnabled ? false : true} />}
                    </ChapterContent>
                ) : null}
                {chatActivated || !mobileMenuEnabled ? (
                    <ChatContent isMobile={mobileMenuEnabled}>
                        <ChatViewer />
                    </ChatContent>
                ) : null}
            </div>
            <PollContainer isOnDemand={false} isLandscape={isLandscape} mobileMenuEnabled={mobileMenuEnabled} />
            {mobileMenuEnabled ? (
                <MobileMenuNavigator
                    onItemClick={onMobileMenuItemClicked}
                    hasSlides={hasSlides}
                    activeSlideNumber={activeSlideNumber}
                    hasChapters={hasChapters}
                    isLandscape={isLandscape}
                    unreadChatMessages={unreadChatMessages}
                />
            ) : null}
        </ViewerLiveWebinarLayout>
    );
};
